import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Save, X, Eye, Trash2, Upload, Download, RotateCcw, Palette,
  FileText, Building2, Receipt, Package, Calculator, Plus,
  ChevronDown, ChevronUp, Search, Edit2, Copy, Grid3X3
} from 'lucide-react';
import {
  ERPInput, ERPSelect, ERPCheckbox, ERPSection,
  ERPNumericInput, ERPDisplayField, ERPModal, ERPButton
} from '../shared/ERPComponents.jsx';
import { gridLayouts } from '../shared/ERPStyles';

// Initial form state
const initialDocumentState = {
  piNo: '',
  piDate: new Date().toISOString().split('T')[0],
  invoiceDate: new Date().toISOString().split('T')[0],
  mrlrRef: '',
  payMode: 'credit',
  supplier: '',
  purchaseAc: 'PURCHASES',
  remarks: '',
  grcInformation: '',
  invAmt: '',
};

// Initial item row
const createEmptyRow = (id) => ({
  id,
  itemCode: '',
  hsnCode: '',
  qty: '',
  rate: '',
  mrp: '',
  value: 0,
  purDisc: '',
  purDiscType: 'percent',
  gstPercent: '',
  // Item info fields (shown in left panel when row selected)
  division: '',
  department: '',
  category: '',
  brand: '',
  margin: '',
  size: '',
  color: '',
  design: '',
  cRef: '',
});

// Sample data
const sampleData = {
  payModes: [
    { value: 'credit', label: 'Credit' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
  ],
  suppliers: [
    { value: 'FASHION_CITY', label: 'FASHION CITY', gstin: '07DHCPS2961G1Z6', stateCode: '07 - Delhi', os: -93191.00, total: -93191.00, dbtLmt: 0 },
    { value: 'VENDOR1', label: 'Vendor One', gstin: '29ABCDE1234F1Z5', stateCode: '29 - Karnataka', os: 15000.00, total: 15000.00, dbtLmt: 50000 },
  ],
  gstRates: [
    { value: '0', label: '0%' },
    { value: '5', label: '5%' },
    { value: '12', label: '12%' },
    { value: '18', label: '18%' },
    { value: '28', label: '28%' },
  ],
  discTypes: [
    { value: 'percent', label: '%' },
    { value: 'amount', label: 'Amt' },
  ],
  items: [
    { code: 'ITEM001', hsn: '6114', name: 'Leggings Ankel', rate: 1500, mrp: 2025, gst: 12 },
    { code: 'ITEM002', hsn: '6115', name: 'Jeans Full', rate: 2000, mrp: 2800, gst: 12 },
  ],
};

const PurchaseInvoiceScreen = ({ theme = 'light', onClose }) => {
  const isDark = theme === 'dark';
  const [document, setDocument] = useState(initialDocumentState);
  const [items, setItems] = useState([createEmptyRow(1)]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [errors, setErrors] = useState({});
  const [billDetails, setBillDetails] = useState({
    discPercEnabled: false,
    discPerc: '',
    freight: '',
    freightGst: false,
    roundOff: '',
  });
  const [showItemLookup, setShowItemLookup] = useState(false);

  // Get selected supplier info
  const selectedSupplier = useMemo(() => {
    return sampleData.suppliers.find(s => s.value === document.supplier) || null;
  }, [document.supplier]);

  // Get selected item's info for left panel
  const selectedItem = useMemo(() => {
    return items.find(i => i.id === selectedRowId);
  }, [items, selectedRowId]);

  // Calculate totals
  const calculations = useMemo(() => {
    let totalQty = 0;
    let totalRate = 0;
    let totalMrp = 0;
    let totalValue = 0;
    let totalDisc = 0;
    let totalGst = 0;

    items.forEach(item => {
      const qty = parseFloat(item.qty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const mrp = parseFloat(item.mrp) || 0;
      const purDisc = parseFloat(item.purDisc) || 0;
      const gstPercent = parseFloat(item.gstPercent) || 0;

      const lineValue = qty * rate;
      const discAmount = item.purDiscType === 'percent' ? (lineValue * purDisc / 100) : purDisc;
      const valueAfterDisc = lineValue - discAmount;
      const gstAmount = valueAfterDisc * gstPercent / 100;

      totalQty += qty;
      totalRate += rate;
      totalMrp += mrp;
      totalValue += lineValue;
      totalDisc += discAmount;
      totalGst += gstAmount;
    });

    // Apply bill-level discount
    const billDiscPercent = billDetails.discPercEnabled ? (parseFloat(billDetails.discPerc) || 0) : 0;
    const billDiscount = totalValue * billDiscPercent / 100;
    
    // Freight
    const freight = parseFloat(billDetails.freight) || 0;
    const freightGst = billDetails.freightGst ? freight * 0.18 : 0;
    
    // Round off
    const roundOff = parseFloat(billDetails.roundOff) || 0;

    const grossAmount = totalValue;
    const discountTotal = totalDisc + billDiscount;
    const gstTotal = totalGst + freightGst;
    const netAmount = grossAmount - discountTotal + gstTotal + freight + roundOff;

    // Split GST
    const isInterState = selectedSupplier?.stateCode?.startsWith('07') === false; // Simplified logic
    const cgst = isInterState ? 0 : gstTotal / 2;
    const sgst = isInterState ? 0 : gstTotal / 2;
    const igst = isInterState ? gstTotal : 0;
    const utgst = 0;

    return {
      totalQty,
      totalRate: totalRate.toFixed(2),
      totalMrp: totalMrp.toFixed(2),
      totalValue: totalValue.toFixed(2),
      totalDisc: totalDisc.toFixed(2),
      totalGst: totalGst.toFixed(2),
      grossAmount: grossAmount.toFixed(2),
      discount: discountTotal.toFixed(2),
      gstAmount: gstTotal.toFixed(2),
      netAmount: netAmount.toFixed(2),
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      igst: igst.toFixed(2),
      utgst: utgst.toFixed(2),
      balAmt: (parseFloat(document.invAmt) - netAmount).toFixed(2),
    };
  }, [items, billDetails, document.invAmt, selectedSupplier]);

  // Handle document field change
  const handleDocChange = useCallback((field, value) => {
    setDocument(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle bill details change
  const handleBillChange = useCallback((field, value) => {
    setBillDetails(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle item row change
  const handleItemChange = useCallback((rowId, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id !== rowId) return item;
      
      const updated = { ...item, [field]: value };
      
      // Auto-calculate value
      const qty = parseFloat(updated.qty) || 0;
      const rate = parseFloat(updated.rate) || 0;
      updated.value = qty * rate;
      
      return updated;
    }));
  }, []);

  // Add new row
  const addRow = useCallback(() => {
    const newId = Math.max(...items.map(i => i.id)) + 1;
    setItems(prev => [...prev, createEmptyRow(newId)]);
  }, [items]);

  // Delete row
  const deleteRow = useCallback((rowId) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i.id !== rowId));
    if (selectedRowId === rowId) setSelectedRowId(null);
  }, [items.length, selectedRowId]);

  // Item lookup and auto-fill
  const handleItemLookup = useCallback((rowId, itemCode) => {
    const found = sampleData.items.find(i => i.code === itemCode);
    if (found) {
      handleItemChange(rowId, 'hsnCode', found.hsn);
      handleItemChange(rowId, 'rate', found.rate.toString());
      handleItemChange(rowId, 'mrp', found.mrp.toString());
      handleItemChange(rowId, 'gstPercent', found.gst.toString());
    }
  }, [handleItemChange]);

  // Keyboard navigation in grid
  const handleKeyDown = useCallback((e, rowId, field) => {
    if (e.key === 'Tab' && !e.shiftKey && field === 'gstPercent') {
      e.preventDefault();
      addRow();
    }
    if (e.key === 'Enter' && field === 'itemCode') {
      handleItemLookup(rowId, e.target.value);
    }
  }, [addRow, handleItemLookup]);

  // Save handlers
  const handleSave = () => {
    console.log('Saving invoice:', { document, items, billDetails, calculations });
  };

  const handleClear = () => {
    setDocument(initialDocumentState);
    setItems([createEmptyRow(1)]);
    setBillDetails({
      discPercEnabled: false,
      discPerc: '',
      freight: '',
      freightGst: false,
      roundOff: '',
    });
  };

  // Grid columns configuration
  const gridColumns = [
    { key: 'itemCode', label: 'Item Code', width: 'w-28', type: 'text' },
    { key: 'hsnCode', label: 'HSN Code', width: 'w-24', type: 'text' },
    { key: 'qty', label: 'Qty', width: 'w-20', type: 'number' },
    { key: 'rate', label: 'Rate', width: 'w-24', type: 'number' },
    { key: 'mrp', label: 'MRP', width: 'w-24', type: 'number' },
    { key: 'value', label: 'Value', width: 'w-28', type: 'display' },
    { key: 'purDisc', label: 'Pur Disc', width: 'w-20', type: 'number' },
    { key: 'purDiscType', label: 'Type', width: 'w-16', type: 'select', options: sampleData.discTypes },
    { key: 'gstPercent', label: 'GST %', width: 'w-20', type: 'select', options: sampleData.gstRates },
  ];

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header Toolbar */}
      <div className={`shrink-0 flex items-center justify-between px-4 py-2 border-b ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-600`}>
              <Receipt size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold">Purchase Invoice</h1>
              <p className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Create purchase invoice / GRN</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <ERPButton variant="success" icon={Save} onClick={handleSave} isDark={isDark}>
            Save
          </ERPButton>
          <ERPButton variant="secondary" icon={Eye} isDark={isDark}>
            Show
          </ERPButton>
          <ERPButton variant="danger" icon={Trash2} isDark={isDark}>
            Delete
          </ERPButton>
          <ERPButton variant="primary" icon={Upload} isDark={isDark}>
            Import
          </ERPButton>
          <ERPButton variant="secondary" icon={Download} isDark={isDark}>
            Export
          </ERPButton>
          <ERPButton variant="warning" icon={RotateCcw} onClick={handleClear} isDark={isDark}>
            Clear
          </ERPButton>
          <ERPButton variant="danger" icon={X} onClick={onClose} isDark={isDark}>
            Close
          </ERPButton>
          <div className={`w-px h-6 mx-2 ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
          <ERPButton variant="ghost" icon={Palette} isDark={isDark}>
            Themes
          </ERPButton>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel - Item Info (when row selected) */}
        <div className={`w-40 shrink-0 border-r overflow-y-auto ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-100 border-slate-200'}`}>
          <div className={`px-3 py-2 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <h3 className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Item Info
            </h3>
          </div>
          <div className="p-2 space-y-2">
            {['division', 'department', 'category', 'brand', 'margin', 'size', 'color', 'design', 'cRef'].map(field => (
              <div key={field}>
                <label className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </label>
                <div className={`px-2 py-1 text-[10px] rounded ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-600'}`}>
                  {selectedItem?.[field] || '-'}
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-dashed mt-2" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              <label className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Disc Amt</label>
              <input 
                className={`w-full px-2 py-1 text-[10px] rounded border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`text-[9px] font-bold uppercase ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Scan Doc</label>
              <button className={`w-full px-2 py-1 text-[10px] rounded border flex items-center gap-1 ${isDark ? 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                <Search size={10} /> Browse
              </button>
            </div>
          </div>
        </div>

        {/* Center - Document & Grid */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Top Row: Document Info & Supplier Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Document Information */}
            <ERPSection
              title="Document Information"
              icon={FileText}
              isDark={isDark}
              headerColor="bg-blue-600"
              className="lg:col-span-2"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ERPInput
                  label="P.I No"
                  value={document.piNo}
                  onChange={(v) => handleDocChange('piNo', v)}
                  placeholder="Auto"
                  isDark={isDark}
                />
                <ERPInput
                  label="Invoice Date"
                  value={document.invoiceDate}
                  onChange={(v) => handleDocChange('invoiceDate', v)}
                  type="date"
                  isDark={isDark}
                />
                <ERPInput
                  label="Purchase A/C"
                  value={document.purchaseAc}
                  onChange={(v) => handleDocChange('purchaseAc', v)}
                  isDark={isDark}
                  onLookup={() => {}}
                />
                <ERPInput
                  label="P.I Date"
                  value={document.piDate}
                  onChange={(v) => handleDocChange('piDate', v)}
                  type="date"
                  isDark={isDark}
                />
                <ERPSelect
                  label="Pay Mode"
                  value={document.payMode}
                  onChange={(v) => handleDocChange('payMode', v)}
                  options={sampleData.payModes}
                  isDark={isDark}
                />
                <ERPInput
                  label="Remarks"
                  value={document.remarks}
                  onChange={(v) => handleDocChange('remarks', v)}
                  isDark={isDark}
                />
                <ERPInput
                  label="M.R.L.R.Ref"
                  value={document.mrlrRef}
                  onChange={(v) => handleDocChange('mrlrRef', v)}
                  isDark={isDark}
                />
                <ERPSelect
                  label="Supplier"
                  value={document.supplier}
                  onChange={(v) => handleDocChange('supplier', v)}
                  options={sampleData.suppliers.map(s => ({ value: s.value, label: s.label }))}
                  isDark={isDark}
                  onAdd={() => {}}
                />
                <ERPInput
                  label="GRC Information"
                  value={document.grcInformation}
                  onChange={(v) => handleDocChange('grcInformation', v)}
                  isDark={isDark}
                />
                <ERPNumericInput
                  label="Inv Amt"
                  value={document.invAmt}
                  onChange={(v) => handleDocChange('invAmt', v)}
                  precision={2}
                  isDark={isDark}
                />
                <ERPDisplayField
                  label="Bal Amt"
                  value={calculations.balAmt}
                  isDark={isDark}
                  highlight
                  highlightColor={parseFloat(calculations.balAmt) >= 0 ? 'green' : 'red'}
                />
              </div>
            </ERPSection>

            {/* Supplier Information */}
            <ERPSection
              title="Supplier Information"
              icon={Building2}
              isDark={isDark}
              headerColor="bg-emerald-600"
            >
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>GSTIN</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {selectedSupplier?.gstin || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>St Code</span>
                  <span className={`text-xs ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {selectedSupplier?.stateCode || '-'}
                  </span>
                </div>
                <div className={`h-px ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`} />
                <div className="flex justify-between items-center">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>O/S</span>
                  <span className={`text-xs font-mono ${selectedSupplier?.os < 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {selectedSupplier?.os?.toFixed(2) || '0.00'} {selectedSupplier?.os < 0 ? 'CR' : 'DR'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total</span>
                  <span className={`text-xs font-mono ${selectedSupplier?.total < 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {selectedSupplier?.total?.toFixed(2) || '0.00'} {selectedSupplier?.total < 0 ? 'CR' : 'DR'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Dbt Lmt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {selectedSupplier?.dbtLmt?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </ERPSection>
          </div>

          {/* Item Grid */}
          <ERPSection
            title="Item Details"
            icon={Grid3X3}
            isDark={isDark}
            headerColor="bg-slate-600"
            actions={
              <ERPButton size="sm" variant="ghost" icon={Plus} onClick={addRow} isDark={isDark}>
                Add Row
              </ERPButton>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={isDark ? 'bg-slate-700' : 'bg-amber-100'}>
                    <th className="w-8 px-2 py-2 text-left">#</th>
                    {gridColumns.map(col => (
                      <th key={col.key} className={`${col.width} px-2 py-2 text-left font-bold uppercase tracking-wider`}>
                        {col.label}
                      </th>
                    ))}
                    <th className="w-12 px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr 
                      key={item.id}
                      className={`border-b transition-colors cursor-pointer ${
                        selectedRowId === item.id 
                          ? isDark ? 'bg-blue-900/30' : 'bg-blue-50'
                          : isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'
                      } ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                      onClick={() => setSelectedRowId(item.id)}
                    >
                      <td className={`px-2 py-1 font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {index + 1}
                      </td>
                      {gridColumns.map(col => (
                        <td key={col.key} className="px-1 py-1">
                          {col.type === 'display' ? (
                            <span className={`px-2 py-1 block text-right font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                              {item[col.key]?.toFixed(2) || '0.00'}
                            </span>
                          ) : col.type === 'select' ? (
                            <select
                              value={item[col.key]}
                              onChange={(e) => handleItemChange(item.id, col.key, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, item.id, col.key)}
                              className={`w-full px-2 py-1 rounded border text-xs ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-600 text-white' 
                                  : 'bg-white border-slate-300'
                              }`}
                            >
                              <option value="">-</option>
                              {col.options?.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={col.type}
                              value={item[col.key]}
                              onChange={(e) => handleItemChange(item.id, col.key, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, item.id, col.key)}
                              onBlur={() => col.key === 'itemCode' && handleItemLookup(item.id, item.itemCode)}
                              className={`w-full px-2 py-1 rounded border text-xs ${
                                col.type === 'number' ? 'text-right font-mono' : ''
                              } ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-600 text-white' 
                                  : 'bg-white border-slate-300'
                              }`}
                              placeholder={col.key === 'itemCode' ? 'Enter/Scan' : ''}
                            />
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteRow(item.id); }}
                          className={`p-1 rounded transition-colors ${
                            items.length <= 1 
                              ? 'opacity-30 cursor-not-allowed' 
                              : isDark ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-100 text-red-500'
                          }`}
                          disabled={items.length <= 1}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Grid footer with totals */}
                <tfoot>
                  <tr className={isDark ? 'bg-slate-700 font-bold' : 'bg-slate-100 font-bold'}>
                    <td className="px-2 py-2">Total:</td>
                    <td></td>
                    <td></td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalQty}</td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalRate}</td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalMrp}</td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalValue}</td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalDisc}</td>
                    <td></td>
                    <td className="px-2 py-2 text-right font-mono">{calculations.totalGst}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </ERPSection>

          {/* Bottom Row: Bill Details & Amount Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Bill Details */}
            <ERPSection
              title="Bill Details"
              icon={FileText}
              isDark={isDark}
              headerColor="bg-purple-600"
            >
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <ERPCheckbox
                    label="Disc %"
                    checked={billDetails.discPercEnabled}
                    onChange={() => handleBillChange('discPercEnabled', !billDetails.discPercEnabled)}
                    isDark={isDark}
                  />
                  {billDetails.discPercEnabled && (
                    <input
                      type="number"
                      value={billDetails.discPerc}
                      onChange={(e) => handleBillChange('discPerc', e.target.value)}
                      className={`w-20 px-2 py-1 rounded border text-xs text-right ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                      placeholder="0.00"
                    />
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className={`text-xs w-20 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Freight</label>
                  <input
                    type="number"
                    value={billDetails.freight}
                    onChange={(e) => handleBillChange('freight', e.target.value)}
                    className={`flex-1 px-2 py-1 rounded border text-xs text-right ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                    placeholder="0.00"
                  />
                  <ERPCheckbox
                    label="GST"
                    checked={billDetails.freightGst}
                    onChange={() => handleBillChange('freightGst', !billDetails.freightGst)}
                    isDark={isDark}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className={`text-xs w-20 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Round Off</label>
                  <input
                    type="number"
                    value={billDetails.roundOff}
                    onChange={(e) => handleBillChange('roundOff', e.target.value)}
                    className={`flex-1 px-2 py-1 rounded border text-xs text-right ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                    placeholder="0.00"
                  />
                  <button className={`px-2 py-1 rounded border text-xs ${isDark ? 'border-slate-600 text-slate-400 hover:bg-slate-700' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}>
                    +
                  </button>
                </div>
              </div>
            </ERPSection>

            {/* Tax Summary */}
            <ERPSection
              title="Tax Summary"
              icon={Calculator}
              isDark={isDark}
              headerColor="bg-rose-600"
            >
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>CGST Amt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {calculations.cgst}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>SGST Amt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {calculations.sgst}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>IGST Amt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {calculations.igst}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>UTGST Amt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {calculations.utgst}
                  </span>
                </div>
              </div>
            </ERPSection>

            {/* Amount Summary */}
            <ERPSection
              title="Amount Details"
              icon={DollarSign}
              isDark={isDark}
              headerColor="bg-amber-600"
            >
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Gross Amount</span>
                  <span className={`text-xs font-mono font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    {calculations.grossAmount}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Discount</span>
                  <span className={`text-xs font-mono text-red-500`}>
                    -{calculations.discount}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>GST Amt</span>
                  <span className={`text-xs font-mono ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                    +{calculations.gstAmount}
                  </span>
                </div>
                <div className={`h-px ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
                <div className="flex justify-between items-center">
                  <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Net Amount</span>
                  <span className={`text-sm font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    {calculations.netAmount}
                  </span>
                </div>
              </div>
            </ERPSection>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`shrink-0 px-4 py-2 border-t flex items-center justify-between ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center gap-4 text-[10px]">
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>Tab</kbd> Next Field
          </span>
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>Enter</kbd> Lookup Item
          </span>
          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>Ctrl+V</kbd> Bulk Paste
          </span>
        </div>
        <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          RetailCLIQ ERP • Purchase Module
        </div>
      </div>
    </div>
  );
};

// Helper component for DollarSign icon
const DollarSign = ({ size, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

export default PurchaseInvoiceScreen;
